
import { useCallback } from 'react';
import validationService from '../services/validationService';
import { useAutomationContext } from '../context/AutomationContext';

const STEP_LABELS = ['Trigger', 'Conditions', 'Actions', 'Review'];

export function formatValidationToast(errors, stepIndex) {
  const messages = Object.values(errors || {}).filter(Boolean);
  const stepLabel = STEP_LABELS[stepIndex];

  if (messages.length === 0) {
    return stepLabel
      ? `Please fix validation errors on the ${stepLabel} step before proceeding`
      : 'Please fix validation errors before proceeding';
  }

  const first = messages[0];
  const more =
    messages.length > 1 ? ` (and ${messages.length - 1} more)` : '';

  return stepLabel ? `${stepLabel}: ${first}${more}` : `${first}${more}`;
}

export function useValidation() {
  const { state, actions } = useAutomationContext();

  const validateCurrentStep = useCallback(() => {
    const validation = validationService.validateStep(
      state.activeStep,
      state,
      {
        objectMetadata: state.objectMetadata,
        fieldMetadata: state.fieldMetadata,
        operators: state.operators
      }
    );

    if (!validation.valid) {
      actions.setErrors(validation.errors);
    } else {
      actions.setErrors({});
    }

    return validation;
  }, [state, actions]);

  const validateField = useCallback((fieldName, value) => {
    // Field-level validation
    const errors = { ...state.errors };
    
    if (!value && fieldName.includes('required')) {
      errors[fieldName] = 'This field is required';
    } else {
      delete errors[fieldName];
    }
    
    actions.setErrors(errors);
    return !errors[fieldName];
  }, [state.errors, actions]);

  const clearErrors = useCallback(() => {
    actions.setErrors({});
  }, [actions]);

  const getFieldError = useCallback((fieldName) => {
    return state.errors[fieldName];
  }, [state.errors]);

  return {
    validateCurrentStep,
    validateField,
    clearErrors,
    getFieldError,
    errors: state.errors,
    hasErrors: Object.keys(state.errors).length > 0
  };
}
